// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for creating a new pipeline with custom name, ID, and description.
 */
import { useState } from 'react'
import { Input, Textarea } from '@/components/ui'
import { Field, FormDialog } from '@/components/forms'
import { slugify } from '@/subsystems/layout/utils/layout-utils'

export interface CreatePipelineData {
  id?: string
  name: string
  description?: string
}

interface CreatePipelineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (data: CreatePipelineData) => void
  isPending: boolean
}

export function CreatePipelineDialog({
  open,
  onOpenChange,
  onCreate,
  isPending,
}: CreatePipelineDialogProps) {
  const [pipelineId, setPipelineId] = useState('')
  const [pipelineIdTouched, setPipelineIdTouched] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // The pipeline ID defaults to a slug of the name and tracks it as the user
  // types, until they edit the ID themselves — then it stays put.
  const handleNameChange = (value: string) => {
    setName(value)
    if (!pipelineIdTouched) setPipelineId(slugify(value))
  }

  const handlePipelineIdChange = (value: string) => {
    setPipelineIdTouched(true)
    setPipelineId(value)
  }

  const handleSubmit = () => {
    onCreate({
      id: pipelineId || undefined,
      name,
      description: description || undefined,
    })
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setPipelineId('')
      setPipelineIdTouched(false)
      setName('')
      setDescription('')
    }
    onOpenChange(newOpen)
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={handleOpenChange}
      title="Create Pipeline"
      description="Create a new document processing pipeline."
      onSubmit={handleSubmit}
      submitting={isPending}
      canSubmit={name.trim().length > 0}
      submitLabel="Create"
      submitTestId="builder-pipeline-create-submit"
    >
      <Field label="Name" htmlFor="name">
        <Input
          id="name"
          placeholder="My Pipeline"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          disabled={isPending}
          required
          autoFocus
          data-testid="builder-pipeline-create-name"
        />
      </Field>
      <Field
        label="Pipeline ID"
        htmlFor="pipeline_id"
        helper="Defaults to a slug of the name. Use lowercase letters, numbers, and hyphens."
      >
        <Input
          id="pipeline_id"
          placeholder="my-pipeline"
          value={pipelineId}
          onChange={(e) => handlePipelineIdChange(e.target.value)}
          disabled={isPending}
        />
      </Field>
      <Field label="Description (optional)" htmlFor="description">
        <Textarea
          id="description"
          placeholder="What does this pipeline do?"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isPending}
          rows={3}
        />
      </Field>
    </FormDialog>
  )
}
